<template>
	<TheBox :title="'Zu erledigen'" v-loading="loadingTodos">
		<div class="todos-wrapper">
			<ul v-if="todoList.length > 0" class="todolist">
				<li v-for="li in todoList" :key="li.id" :class="{ 'todoitem--done': li.isDone }">
					<div class="icon-wrapper" @click="() => putTodoDone(li)">
						<CheckIcon v-if="li.isDone" />
					</div>

					<span v-if="!li.isEditing" @click="editTodo(li)">{{ li.title }}</span>
					<InputLite
						v-else
						:inputValue="li.title"
						@inputenter="(val) => updateTodoTitleLocal(li, val)"
						@inputblur="() => putTodoTitle(li)"
						@inputclear="deleteTodoItem(li)"
					/>
				</li>
			</ul>
			<p v-else>{{ texts.todolist.emptyText }}</p>
		</div>

		<InputLite
			:inputValue="newTodoItem"
			placeholder="Aufgabe hier eintragen"
			@inputchange="(val) => (newTodoItem = val)"
			@inputenter="createTodo"
			@inputclear="newTodoItemErr = false"
			:showErr="newTodoItemErr"
			:errMsg="texts.formErrors.newTodoItemFailed"
			ref="inputLiteNewTodo"
		>
			<el-icon @click="createTodo"><Plus /></el-icon>
		</InputLite>
	</TheBox>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import TheBox from './TheBox.vue';
import InputLite from './forms/InputLite.vue';

import { texts } from '../utils/texts';
import { createTodo, deleteTodo, getTodos, updateTodoDone, updateTodoTitle } from '@/requests/todos';
import { TodoItem } from '../types/types';
import { Plus } from '@element-plus/icons-vue';
import CheckIcon from '../assets/icons/CheckIcon.vue';

export default defineComponent({
	name: 'TheTodoList',
	data() {
		return {
			loadingTodos: false,
			todoList: [] as TodoItem[],
			newTodoItem: '',
			newTodoItemErr: false,
			todoOrder: [] as string[],
		};
	},
	components: {
		TheBox,
		Plus,
		CheckIcon,
		InputLite,
	},
	computed: {
		texts() {
			return texts;
		},
	},
	methods: {
		sortTodosByOriginalOrder() {
			this.todoList.sort((a, b) => {
				return this.todoOrder.indexOf(a.id) - this.todoOrder.indexOf(b.id);
			});
		},
		async putTodoDone(todoItem) {
			const item = this.todoList.find((t) => t.id === todoItem.id);
			if (item) {
				this.loadingTodos = true;
				const putTodoResponse = await updateTodoDone(todoItem.id, !todoItem.isDone);

				if (putTodoResponse.success) {
					this.todoList = putTodoResponse.data;
					this.sortTodosByOriginalOrder();
				} else {
					console.error('putTodoResponse', putTodoResponse.error);
				}
				this.loadingTodos = false;
			}
		},
		editTodo(todoItem) {
			const item = this.todoList.find((t) => t.id === todoItem.id);
			if (item && !item?.isDone) {
				item.isEditing = true;
			}
		},
		updateTodoTitleLocal(todoItem, title) {
			const item = this.todoList.find((t) => t.id === todoItem.id);
			if (item) {
				item.title = title;
			}
		},
		clearTodoInput() {
			this.newTodoItemErr = false;
			this.newTodoItem = '';
		},
		async putTodoTitle(todoItem) {
			const item = this.todoList.find((t) => t.id === todoItem.id);
			if (item) {
				this.loadingTodos = true;
				const putTodoResponse = await updateTodoTitle(todoItem.id, todoItem.title);
				if (putTodoResponse.success) {
					this.todoList = putTodoResponse.data;
					this.sortTodosByOriginalOrder();
				} else {
					console.error('putTodoResponse', putTodoResponse.error);
				}
				item.isEditing = false;
				this.loadingTodos = false;
			}
		},
		async deleteTodoItem(todoItem) {
			this.loadingTodos = true;
			const deleteTodoResponse = await deleteTodo(todoItem.id);
			if (deleteTodoResponse.success) {
				this.todoList = deleteTodoResponse.data;
				this.sortTodosByOriginalOrder();
			} else {
				console.error('deleteTodoResponse', deleteTodoResponse.error);
			}
			this.loadingTodos = false;
		},
		async createTodo() {
			if (!this.newTodoItem) return;

			this.loadingTodos = true;
			const createTodoResponse = await createTodo(this.newTodoItem);

			if (createTodoResponse.success) {
				this.newTodoItem = '';
				this.newTodoItemErr = false;
				this.todoList = createTodoResponse.data;
				this.sortTodosByOriginalOrder();
			} else {
				this.newTodoItemErr = true;
			}
			this.loadingTodos = false;
		},
		async loadTodos() {
			this.loadingTodos = true;
			const todoResponse = await getTodos();

			if (todoResponse.success) {
				this.todoList = todoResponse.data.map((t) => {
					return {
						...t,
						isEditing: false,
					};
				});
				this.todoOrder = this.todoList.map((t) => t.id);
			} else {
				console.error('todoResponse', todoResponse.error);
				this.todoList = [];
			}
			this.loadingTodos = false;
		},
	},
	mounted() {
		this.loadTodos();
	},
});
</script>
